export interface WorkspaceScope<V extends string> {
  visibility: V;
  workspaceIds: string[];
}

export interface WorkspaceScopeDelta<V extends string> {
  visibility?: V;
  add?: string[];
  remove?: string[];
}

/**
 * The change that takes `current` to `wanted`, or null when there is none.
 *
 * Both lists are differences against what the API reported, which is what leaves a workspace the
 * caller cannot see in neither of them. Going to `organizationVisibility` leaves the grants alone so
 * the list survives a switch back: treating them as the wanted set is what keeps them out of
 * `remove`, where deriving `remove` from an empty set would revoke every grant.
 */
export function diffWorkspaceScope<V extends string>(
  current: WorkspaceScope<V>,
  wanted: WorkspaceScope<V>,
  organizationVisibility: V,
): WorkspaceScopeDelta<V> | null {
  const desired =
    wanted.visibility === organizationVisibility ? current.workspaceIds : wanted.workspaceIds;

  const add = desired.filter((id) => !current.workspaceIds.includes(id));
  const remove = current.workspaceIds.filter((id) => !desired.includes(id));
  const visibilityChanged = wanted.visibility !== current.visibility;

  if (!add.length && !remove.length && !visibilityChanged) return null;

  return {
    ...(visibilityChanged ? { visibility: wanted.visibility } : {}),
    ...(add.length ? { add } : {}),
    ...(remove.length ? { remove } : {}),
  };
}
